import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { PortalComponent } from './portal.component';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { PortalRoutingModule } from './portal-routing.module';
import { NavbarComponent } from './components/navbar/navbar.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { SidebarListComponent } from './components/sidebar/sidebar-list/sidebar-list.component';
import { SidebarMobileHeaderComponent } from './components/sidebar/sidebar-mobile-header/sidebar-mobile-header.component';
import { PortalSidebarDrawerComponent } from './components/sidebar/portal-sidebar-drawer/portal-sidebar-drawer.component';
import { PortalHomeComponent } from './pages/portal-home/portal-home.component';
import { DatadogRumService } from 'src/app/shared/services/datadog.service';
import { WINDOW_PROVIDERS } from 'src/app/shared/utils/window.provider';
import { AuthModule, StsConfigLoader } from 'angular-auth-oidc-client';
import { PortalAuthConfigLoader } from './config/portal-auth-config.loader';
import { PortalInitializationService } from './config/portal-initialization.service';
import { PortalLoginComponent } from './pages/portal-login/portal-login.component';
import { PortalTokenInterceptor } from './auth/portal-token.interceptor';
import { CredentialsComponent } from './pages/credentials/credentials.component';
import { PortalTeamsComponent } from './pages/portal-teams/portal-teams.component';
import { PortalProfileComponent } from './pages/portal-profile/portal-profile.component';
import { PortalAddressFormatPipe } from './pages/portal-profile/portal-address-format.pipe';
import { PortalProfileNamePipe } from './pages/portal-profile/portal-profile-name.pipe';
import { SharedAppPortalModule } from 'src/shared-app-portal/shared-app-portal.module';
import { UpdatePortalProfileDialogComponent } from './dialogs/update-portal-profile.dialog/update-portal-profile.dialog.component';
import { VerifyEmailDialogComponent } from './dialogs/verify-email.dialog/verify-email.dialog.component';
import { PortalFormComponent } from './pages/portal-form/portal-form.component';
import { PortalTasksComponent } from './components/portal-tasks/portal-tasks.component';
import { PortalDashboardCredentialComponent } from './components/portal-dashboard-credential/portal-dashboard-credential.component';
import { MessagesComponent } from './pages/messages/messages.component';
import { MessagePortalContentComponent } from 'src/portal/components/messeges-content/message-portal-content.component';
import { PortalInProgressCredentialComponent } from './components/portal-in-progress-credential/portal-in-progress-credential.component';
import { AttributeService } from 'src/app/shared/services/attribute.service';
import { PortalAttributeService } from './shared-services/portal-attribute.service';
import { AWSService } from 'src/app/shared/services/aws.service';
import { PortalAwsService } from './shared-services/portal-aws.service';
import { CustomDocumentService } from 'src/app/shared/services/custom-document.service';
import { PortalCustomDocumentService } from './shared-services/portal-custom-document.service';
import { PortalPaymentsComponent } from './pages/payment/portal-payments.component';
import { PortalProfileSystemFieldsComponent } from './components/portal-profile-system-fields/portal-profile-system-fields.component';
import { PortalTeamCredentialsComponent } from './pages/portal-team-manager-pages/portal-team-credentials/portal-team-credentials.component';
import { PortalTeamPaymentsComponent } from './pages/portal-team-manager-pages/portal-team-payments/portal-team-payments.component';
import { DatePipe } from '@angular/common';
import { PortalAuthService } from './shared-services/portal-auth.service';
import { AuthService } from 'src/app/auth/auth.service';
import { MemberEvaluationService } from 'src/app/members/member-evaluation.service';
import { PortalEvaluationService } from './shared-services/portal-evaluation.service';
import { SurveyService } from 'src/app/shared/services/survey.service';
import { PortalSurveyService } from './shared-services/portal-survey.service';
import { PortalDashboardCTAComponent } from './components/porta-dashboard-cta/portal-dashboard-cta.component';
import { PortalFormDetailsComponent } from './components/portal-form-details/portal-form-details.component';
import { Title } from '@angular/platform-browser';
import { NgOtpInputModule } from 'ng-otp-input';
import { PortalTeamManagerMainPageComponent } from './pages/portal-team-manager-pages/portal-team-manager-main-page.component';

@NgModule({
  declarations: [
    PortalComponent,
    NavbarComponent,
    DashboardComponent,
    SidebarListComponent,
    SidebarMobileHeaderComponent,
    PortalSidebarDrawerComponent,
    PortalHomeComponent,
    PortalLoginComponent,
    CredentialsComponent,
    PortalTeamsComponent,
    MessagesComponent,
    PortalProfileComponent,
    PortalAddressFormatPipe,
    PortalProfileNamePipe,
    UpdatePortalProfileDialogComponent,
    VerifyEmailDialogComponent,
    PortalFormComponent,
    PortalTasksComponent,
    PortalDashboardCredentialComponent,
    MessagePortalContentComponent,
    PortalInProgressCredentialComponent,
    PortalPaymentsComponent,
    PortalProfileSystemFieldsComponent,
    PortalDashboardCTAComponent,
    PortalFormDetailsComponent,
    PortalTeamManagerMainPageComponent,
    PortalTeamCredentialsComponent,
    PortalTeamPaymentsComponent
  ],
  imports: [
    BrowserAnimationsModule,
    SharedAppPortalModule,
    PortalRoutingModule,
    NgOtpInputModule,
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useClass: PortalAuthConfigLoader
      }
    })
  ],
  providers: [
    Title,
    PortalAuthConfigLoader,
    provideAppInitializer(() => {
      const appInit = inject(PortalInitializationService);
      const dataDogRum = inject(DatadogRumService);
      return appInit.load().then((appConfig) => {
        if (appConfig?.initialized) {
          dataDogRum.initLineupPortal(appConfig);
        }
      });
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PortalTokenInterceptor,
      multi: true
    },
    {
      provide: AttributeService,
      useClass: PortalAttributeService // Use PortalAttributeService instead of AttributeService for some shared components between portal and app that are using AttributeService
    },
    {
      provide: AWSService,
      useClass: PortalAwsService // Use PortalAwsService instead of AWSService for some shared components between portal and app that are using AWSService
    },
    {
      provide: CustomDocumentService,
      useClass: PortalCustomDocumentService // Use PortalCustomDocumentService instead of CustomDocumentService for some shared components between portal and app that are using CustomDocumentService
    },
    {
      provide: AuthService,
      useClass: PortalAuthService // Use PortalAuthService instead of AuthService for some shared components between portal and app that are using AuthService
    },
    {
      provide: MemberEvaluationService,
      useClass: PortalEvaluationService // Use PortalEvaluationService instead of MemberEvaluationService for some shared components between portal and app that are using MemberEvaluationService
    },
    {
      provide: SurveyService,
      useClass: PortalSurveyService // Use PortalSurveyService instead of SurveyService for some shared components between portal and app that are using SurveyService
    },
    DatePipe,
    WINDOW_PROVIDERS
  ],
  bootstrap: [PortalComponent]
})
export class PortalModule {}
